import { inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import {
  ShiftValidationDetails,
  ShiftValidationsByShiftId,
} from '@wilson/interfaces';
import { CreateNewViaDragAndDropService } from '@wilson/shift-timeline/services';
import { ShiftsService } from '@wilson/shifts';
import { chunk } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  debounceTime,
  finalize,
  from,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

export type ShiftId = string;

export interface QueueValidationRequestOptions {
  forceRefresh?: boolean;
  previouslyVisibleShiftIds?: ShiftId[];
}

type ChunkedValidationRequest = {
  request: Observable<ShiftValidationsByShiftId>;
  ids: ShiftId[];
};

/**
 * Abstract base for assigned and unassigned shift validation state services.
 * State and dependencies live here; subclasses only need @Injectable and to extend the base.
 *
 * ## Why there are two separate request queues
 *
 * This service fetches validations through two independent pipelines, named for the one attribute
 * that actually distinguishes them: whether a queued or in-flight request can be interrupted
 * (cancelled, losing its result) by a newer one, or not.
 *
 * 1. **The interruptible queue** (`interruptibleRequestQueueMapSubject`, `switchMap`-based) is used
 *    for scroll/viewport-driven requests: "which shifts are currently visible, and what are their
 *    validations?" As the user scrolls, shifts constantly enter and leave the viewport. A shift
 *    that has just scrolled out of view no longer matters - there's no reason to let an in-flight
 *    request for it keep running, and doing so would let fast scrolling flood the backend with
 *    requests for shifts nobody is looking at anymore. `switchMap` cancelling the previous batch
 *    whenever a new one arrives is the correct, deliberate behavior here, not a bug.
 *
 * 2. **The uninterruptible queue** (`uninterruptibleRequestSubject`, `concatMap`-based) is used
 *    after a shift, absence, or activity mutation has been confirmed by the backend: "go refresh
 *    validations for everyone that touches." Unlike a scroll event, a mutation's relevance never
 *    expires just because another mutation happens shortly after: if a result from an earlier
 *    action gets cancelled and silently dropped, the user is left looking at a shift whose
 *    validation never updated to reflect the action they just took, with no visible indication
 *    anything went wrong. So this queue must never interrupt in-flight or queued work - a batch
 *    that arrives while a previous one is still processing waits in line and runs after, rather
 *    than pre-empting it. A plain `Subject` (no "current value" to overwrite, unlike
 *    `BehaviorSubject`) combined with `concatMap` (which waits for the current inner Observable to
 *    complete before starting the next, rather than unsubscribing from it) gives exactly that:
 *    every call to `queueUninterruptibleValidationRequest` is guaranteed to eventually run to
 *    completion, in the order it was made.
 *
 * ## Why loading state is tracked separately per queue
 *
 * Both queues ultimately feed the same `shiftValidationsRecord` (merged additively, so this is
 * safe), but a shiftId can legitimately be in flight in *both* queues at once (e.g. visible during
 * a scroll and also the target of a mutation). If loading state were a single shared Set, the
 * interruptible queue cancelling its own in-flight batch (intentional, see above) would clear
 * every id in that batch from loading state - including ids that are *also* still genuinely in
 * flight via the uninterruptible queue - incorrectly clearing their loading indicator before the
 * mutation's result has actually arrived. `interruptibleLoadingIds`/`uninterruptibleLoadingIds`
 * are tracked as separate internal sets so each queue can only ever release its own claim; the
 * public `shiftValidationLoadingIds` is their union, so a shift shows as loading for as long as
 * *either* queue still has it in flight.
 *
 * ## Why responses are version-stamped per shift
 *
 * The two queues are otherwise uncoordinated: nothing stops the same shift from being in flight on
 * both at once (e.g. visible during a scroll *and* the target of a mutation), and whichever
 * response lands last wins, regardless of which one actually reflects more recent backend state. A
 * slower, older (non-forced) interruptible response resolving after a forced uninterruptible
 * refresh for the same shift would silently overwrite the fresh, deliberately-forced data with a
 * stale value - exactly the kind of regression this class exists to prevent.
 *
 * `shiftRequestVersions` is a per-shift counter bumped every time a request is actually built for
 * that shift (in `buildChunkedValidationRequests`, by either queue). Each built request's snapshot
 * of the versions it was stamped with is kept in `requestVersionsByObservable`, keyed by the
 * request observable itself. When a response lands, a shift's value is only applied if its stamped
 * version still matches the *current* version for that shift - if a newer request has been built
 * for it since, this response is stale and is dropped instead of applied; the newer request will
 * supply the up-to-date result when it resolves.
 */
export abstract class TimelineShiftValidationsStateServiceBase {
  protected readonly shiftsService = inject(ShiftsService);
  protected readonly messageService = inject(NzMessageService);
  protected readonly translateService = inject(TranslateService);

  protected interruptibleRequestQueueMapSubject = new BehaviorSubject<
    Map<Observable<ShiftValidationsByShiftId>, ShiftId[]>
  >(new Map());
  private interruptibleValidationQueueSubscription!: Subscription;

  // See class doc "Why there are two separate request queues".
  private uninterruptibleRequestSubject = new Subject<
    ChunkedValidationRequest[]
  >();
  private uninterruptibleValidationQueueSubscription!: Subscription;

  protected successfullyValidatedShiftIds = new Set<ShiftId>();
  protected errorLoadingValidationShiftIds$ = new BehaviorSubject<Set<ShiftId>>(
    new Set(),
  );

  // See class doc "Why loading state is tracked separately per queue". Internal only - external
  // code only ever reads the combined `shiftValidationLoadingIds`/`shiftValidationLoadingIds$`.
  private interruptibleLoadingIds = new Set<ShiftId>();
  private uninterruptibleLoadingIds = new Set<ShiftId>();

  // See class doc "Why responses are version-stamped per shift".
  private shiftRequestVersions = new Map<ShiftId, number>();
  private requestVersionsByObservable = new WeakMap<
    Observable<ShiftValidationsByShiftId>,
    Map<ShiftId, number>
  >();

  /** Declared in child services so each has its own instance. */
  abstract shiftValidationLoadingIds: Set<ShiftId>;
  abstract shiftValidationLoadingIds$: Observable<Set<ShiftId>>;
  abstract shiftValidationsRecord: Record<ShiftId, ShiftValidationDetails[]>;
  abstract shiftValidationsRecord$: Observable<
    Record<ShiftId, ShiftValidationDetails[]>
  >;

  // --- Shared public API ---
  isLoading(shiftId: ShiftId): Observable<boolean> {
    return this.shiftValidationLoadingIds$.pipe(
      map((loadingRecord) => loadingRecord.has(shiftId)),
    );
  }

  isLoadingCount(shiftIds: ShiftId[]): Observable<number> {
    return this.shiftValidationLoadingIds$.pipe(
      debounceTime(100),
      map(
        (loadingRecord) =>
          shiftIds.filter((shiftId) => loadingRecord.has(shiftId)).length,
      ),
    );
  }

  hasShiftValidationError(shiftId: ShiftId): Observable<boolean> {
    return this.errorLoadingValidationShiftIds$.pipe(
      map((errorLoadingValidationShiftIds) =>
        errorLoadingValidationShiftIds.has(shiftId),
      ),
    );
  }

  validations(shiftId: ShiftId): Observable<ShiftValidationDetails[]> {
    return this.shiftValidationsRecord$.pipe(map((record) => record[shiftId]));
  }

  hasShiftValidated(shiftId: ShiftId): Observable<boolean> {
    return this.shiftValidationsRecord$.pipe(
      map((record) => shiftId in record),
    );
  }

  keepOnlyVisibleShiftsValidations(shiftIdsToKeep: ShiftId[]): void {
    const record = this.shiftValidationsRecord;
    const next: Record<ShiftId, ShiftValidationDetails[]> = {};
    shiftIdsToKeep.forEach((shiftId) => {
      if (shiftId in record) {
        next[shiftId] = record[shiftId];
      } else {
        this.successfullyValidatedShiftIds.delete(shiftId);
      }
    });
    this.shiftValidationsRecord = next;
  }

  turnOnValidationQueue(): void {
    this.interruptibleValidationQueueSubscription?.unsubscribe();
    this.uninterruptibleValidationQueueSubscription?.unsubscribe();

    // --- Interruptible queue: cancels superseded work by design, see class doc. ---
    this.interruptibleValidationQueueSubscription =
      this.interruptibleRequestQueueMapSubject
        .pipe(
          debounceTime(100),
          switchMap((requestQueueMap) => {
            const entries = Array.from(requestQueueMap.entries());
            const allShiftIds = entries.flatMap(([, chunkedIds]) => chunkedIds);
            this.updateInterruptibleLoadingRecord(allShiftIds);

            return from(entries).pipe(
              concatMap(([request, chunkedIds]) =>
                this.runValidationRequest(request, chunkedIds, (ids) =>
                  this.removeInterruptibleLoadingRecord(ids),
                ),
              ),
              finalize(() => {
                this.removeInterruptibleLoadingRecord(allShiftIds);
              }),
            );
          }),
        )
        .subscribe();

    // --- Uninterruptible queue: never cancels in-flight or queued work, see class doc. A plain
    // Subject (no stored "current value" to overwrite) + concatMap (waits for the current batch to
    // finish before starting the next, never unsubscribes from it) guarantees every call to
    // queueUninterruptibleValidationRequest eventually runs, in the order it was made. ---
    this.uninterruptibleValidationQueueSubscription =
      this.uninterruptibleRequestSubject
        .pipe(
          concatMap((entries) => {
            const allShiftIds = entries.flatMap(({ ids }) => ids);
            this.updateUninterruptibleLoadingRecord(allShiftIds);

            return from(entries).pipe(
              concatMap(({ request, ids }) =>
                this.runValidationRequest(request, ids, (settledIds) =>
                  this.removeUninterruptibleLoadingRecord(settledIds),
                ),
              ),
            );
          }),
        )
        .subscribe();
  }

  /**
   * Runs a single chunked validation request and merges its result, shared by both queues.
   *
   * Deliberately a plain `.pipe()` chain rather than a manual `new Observable((subscriber) =>
   * request.subscribe(...))` wrapper: unsubscribing a `.pipe()` chain propagates all the way down
   * to `request` automatically, which is what actually cancels the underlying HTTP call when the
   * interruptible queue's `switchMap` supersedes this chunk. A manual wrapper has no teardown
   * hook unless you write one, so the superseded request used to keep running in the background
   * and its result could still land - and overwrite fresher data - after the pipeline had already
   * moved on.
   */
  private runValidationRequest(
    request: Observable<ShiftValidationsByShiftId>,
    ids: ShiftId[],
    onSettled: (ids: ShiftId[]) => void,
  ): Observable<void> {
    const versions = this.requestVersionsByObservable.get(request);
    return request.pipe(
      tap((validations) => {
        // See class doc "Why responses are version-stamped per shift": only apply a shift's
        // value if no newer request has been built for it since this one was - otherwise this
        // response is stale, and the newer request will supply the current result instead.
        const freshValidations: ShiftValidationsByShiftId = {};
        Object.entries(validations).forEach(([shiftId, details]) => {
          if (
            versions?.get(shiftId) === this.shiftRequestVersions.get(shiftId)
          ) {
            freshValidations[shiftId] = details;
            this.successfullyValidatedShiftIds.add(shiftId);
          }
        });
        this.shiftValidationsRecord = {
          ...this.shiftValidationsRecord,
          ...freshValidations,
        };
        this.removeErrorLoadingValidationShiftIds(ids);
      }),
      map(() => void 0),
      catchError((error) => {
        this.messageService.error(
          this.translateService.instant(
            'page.shift_timeline.error.fetch_validation_failed',
          ),
        );
        this.updateErrorLoadingValidationShiftIds(ids);
        Sentry.captureException(error);
        return of(void 0);
      }),
      finalize(() => {
        this.requestVersionsByObservable.delete(request);
        onSettled(ids);
      }),
    );
  }

  turnOffValidationQueue(): void {
    this.interruptibleValidationQueueSubscription?.unsubscribe();
    this.uninterruptibleValidationQueueSubscription?.unsubscribe();
    this.successfullyValidatedShiftIds.clear();
    this.interruptibleRequestQueueMapSubject.next(new Map());
    this.interruptibleLoadingIds.clear();
    this.uninterruptibleLoadingIds.clear();
    this.recomputeLoadingRecord();
  }

  clearCache(): void {
    this.successfullyValidatedShiftIds.clear();
    this.shiftValidationsRecord = {};
    this.interruptibleLoadingIds.clear();
    this.uninterruptibleLoadingIds.clear();
    this.recomputeLoadingRecord();
    this.errorLoadingValidationShiftIds$.next(new Set());
  }

  /**
   * Queue a validation request for the given shift IDs through the interruptible queue - use this
   * for visibility-driven requests only. It will be processed eventually if the queue is turned
   * on, and may be cancelled outright if a newer request supersedes it before it runs - see class
   * doc. For a request that must not be cancelled (anything following a shift, absence, or
   * activity mutation), use `queueUninterruptibleValidationRequest` instead.
   */
  queueInterruptibleValidationRequest(
    shiftIds: ShiftId[],
    options?: QueueValidationRequestOptions,
  ): void {
    if (options?.forceRefresh) {
      this.removeInterruptibleLoadingRecord(shiftIds);
    }
    const chunkedRequests = this.buildChunkedValidationRequests(
      shiftIds,
      options,
    );
    const requestQueueMap = new Map<
      Observable<ShiftValidationsByShiftId>,
      ShiftId[]
    >();
    chunkedRequests.forEach(({ request, ids }) => {
      requestQueueMap.set(request, ids);
    });
    this.interruptibleRequestQueueMapSubject.next(requestQueueMap);
  }

  /**
   * Queue a validation request for the given shift IDs through the uninterruptible queue - use
   * this after a shift, absence, or activity mutation has been confirmed by the backend. Unlike
   * `queueInterruptibleValidationRequest`, this is guaranteed to run to completion even if further
   * mutations happen while it's still in flight or still queued - see class doc "Why there are two
   * separate request queues".
   */
  queueUninterruptibleValidationRequest(
    shiftIds: ShiftId[],
    options?: QueueValidationRequestOptions,
  ): void {
    if (options?.forceRefresh) {
      this.removeUninterruptibleLoadingRecord(shiftIds);
    }
    const chunkedRequests = this.buildChunkedValidationRequests(
      shiftIds,
      options,
    );
    if (chunkedRequests.length) {
      this.uninterruptibleRequestSubject.next(chunkedRequests);
    }
  }

  private buildChunkedValidationRequests(
    shiftIds: ShiftId[],
    options: QueueValidationRequestOptions | undefined,
  ): ChunkedValidationRequest[] {
    if (options?.forceRefresh) {
      shiftIds.forEach((shiftId) => {
        this.successfullyValidatedShiftIds.delete(shiftId);
      });
      const next = { ...this.shiftValidationsRecord };
      shiftIds.forEach((shiftId) => delete next[shiftId]);
      this.shiftValidationsRecord = next;
    }

    const shiftIdsThatHaveNotBeenValidated = shiftIds.filter((shiftId) =>
      this.doesShiftNeedValidation(shiftId),
    );

    // This is an edge case that happens on slow network. Validations for visible shifts are somehow canceled before they get any data.
    // One potential reason is that the user scrolls a little bit at a time, and triggers cancellation of requests for shifts that are still on the screen.
    // This causes these shifts to not have any validations at all.
    const visibleShiftIdsThatHaveNotBeenValidated =
      options?.previouslyVisibleShiftIds?.filter((shiftId) =>
        this.doesShiftNeedValidation(shiftId),
      ) || [];

    const chunkedShiftIds = chunk(
      [
        ...shiftIdsThatHaveNotBeenValidated,
        ...visibleShiftIdsThatHaveNotBeenValidated,
      ],
      50,
    );

    return chunkedShiftIds.map((ids) => {
      const request = this.shiftsService.getShiftsValidations(ids);
      this.requestVersionsByObservable.set(
        request,
        this.bumpShiftVersions(ids),
      );
      return { request, ids };
    });
  }

  // See class doc "Why responses are version-stamped per shift".
  private bumpShiftVersions(ids: ShiftId[]): Map<ShiftId, number> {
    const versions = new Map<ShiftId, number>();
    ids.forEach((shiftId) => {
      const nextVersion = (this.shiftRequestVersions.get(shiftId) ?? 0) + 1;
      this.shiftRequestVersions.set(shiftId, nextVersion);
      versions.set(shiftId, nextVersion);
    });
    return versions;
  }

  private doesShiftNeedValidation(shiftId: ShiftId): boolean {
    return (
      !this.successfullyValidatedShiftIds.has(shiftId) &&
      !this.shiftValidationsRecord[shiftId] &&
      shiftId !== CreateNewViaDragAndDropService.MOCK_SHIFT_ID
    );
  }

  private updateErrorLoadingValidationShiftIds(shiftIds: ShiftId[]): void {
    this.errorLoadingValidationShiftIds$.next(
      new Set([...this.errorLoadingValidationShiftIds$.value, ...shiftIds]),
    );
  }

  private removeErrorLoadingValidationShiftIds(shiftIds: ShiftId[]): void {
    this.errorLoadingValidationShiftIds$.next(
      new Set(
        [...this.errorLoadingValidationShiftIds$.value].filter(
          (shiftId) => !shiftIds.includes(shiftId),
        ),
      ),
    );
  }

  private updateInterruptibleLoadingRecord(shiftIds: ShiftId[]): void {
    shiftIds.forEach((shiftId) => this.interruptibleLoadingIds.add(shiftId));
    this.recomputeLoadingRecord();
  }

  private removeInterruptibleLoadingRecord(shiftIds: ShiftId[]): void {
    shiftIds.forEach((shiftId) => this.interruptibleLoadingIds.delete(shiftId));
    this.recomputeLoadingRecord();
  }

  private updateUninterruptibleLoadingRecord(shiftIds: ShiftId[]): void {
    shiftIds.forEach((shiftId) => this.uninterruptibleLoadingIds.add(shiftId));
    this.recomputeLoadingRecord();
  }

  private removeUninterruptibleLoadingRecord(shiftIds: ShiftId[]): void {
    shiftIds.forEach((shiftId) =>
      this.uninterruptibleLoadingIds.delete(shiftId),
    );
    this.recomputeLoadingRecord();
  }

  // See class doc "Why loading state is tracked separately per queue": a shift is loading if
  // either queue still has it in flight, so the public record is the union of both internal sets.
  private recomputeLoadingRecord(): void {
    this.shiftValidationLoadingIds = new Set([
      ...this.interruptibleLoadingIds,
      ...this.uninterruptibleLoadingIds,
    ]);
  }
}
